import { TableCollapsable } from '@octano/global-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Col,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
} from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../../../utils/dates';
import { TranslationsKeys } from '../../../../../../../locales/translations';
import { useCreateEvaluationFormContext } from '../../context/CreateEvaluationFormContext';
import usePartialEvaluationGradeColumns from '../../hooks/usePartialEvaluationColumns';
import useStructureTypeIsFixed from '../../hooks/useStructureTypeIsFixed';
import { EvaluationType, PartialEvaluation } from '../../types';
import sumPercentage from '../../utils/sumPercentage';
import TotalPercentageFooter from '../TotalPercentageFooter';
import SubEvaluationsTable from './SubEvaluationsTable';
import OnlineFixedFooter from './OnlineFixedFooter';
import useOnlineDeliveryModeFixedStructure from '../../hooks/useOnlineDeliveryModeFixedStructure';

export default function EvaluationsTable() {
  const { t } = useTranslation(
    TranslationsKeys.CONFIGURATION_GRADING_STRUCTURE,
  );
  const { isFixedType } = useStructureTypeIsFixed();
  const {
    isOnlineDeliveryModeAndFixedStructure,
  } = useOnlineDeliveryModeFixedStructure();

  const {
    gradeEntry: { testGrades: partialEvaluations },
    onChangePartialEvaluationGradeEntry: onChange,
    handleError: { partialEvaluationGradeEntry: error },
  } = useCreateEvaluationFormContext();

  // Callbacks
  const setPartialEvaluationByName = useCallback(
    (row: { name?: string }) => {
      return (e: any) => {
        row.name = e.target.value;
        onChange([...partialEvaluations]);
      };
    },
    [partialEvaluations, onChange],
  );

  const setPartialEvaluationByDate = useCallback(
    (row: { date?: string | Date | null }) => {
      return (value: any) => {
        row.date = toDateOnlyPayload(value);
        onChange([...partialEvaluations]);
      };
    },
    [partialEvaluations, onChange],
  );

  const setPartialEvaluationByWeighting = useCallback(
    (row: { percentage?: string }) => {
      return (e: any) => {
        let value = e.target.value;
        if (
          Number(value) <= 100 &&
          Number(value) >= 0 &&
          value.search(/\D/) === -1
        ) {
          row.percentage = value;
          onChange([...partialEvaluations]);
        }
      };
    },
    [partialEvaluations, onChange],
  );

  const setPartialEvaluationByLimitDate = useCallback(
    (row: { limitDate?: string | Date | null }) => {
      return (value: any) => {
        row.limitDate = toDateOnlyPayload(value);
        onChange([...partialEvaluations]);
      };
    },
    [partialEvaluations, onChange],
  );

  const setPartialEvaluationByHasLimitDate = useCallback(
    (row: { hasLimitDate?: boolean }) => {
      return (e: any) => {
        row.hasLimitDate = e.target.checked;
        onChange([...partialEvaluations]);
      };
    },
    [partialEvaluations, onChange],
  );

  const columns = usePartialEvaluationGradeColumns({
    onChange,
    error,
    partialEvaluations,
    onNameChange: setPartialEvaluationByName,
    onDateChange: setPartialEvaluationByDate,
    onWeightingChange: setPartialEvaluationByWeighting,
    onHasLimitDateChange: setPartialEvaluationByHasLimitDate,
    onLimitDateChange: setPartialEvaluationByLimitDate,
  });

  // Crud operations - Evaluations
  const addPartialEvaluation = useCallback(() => {
    onChange([
      ...partialEvaluations,
      { type: EvaluationType.Normal, canItBeRemoved: !isFixedType },
    ]);
  }, [partialEvaluations, isFixedType, onChange]);
  const addPartialEvaluationSubevaluations = useCallback(() => {
    onChange([
      ...partialEvaluations,
      {
        type: EvaluationType.Subevaluations,
        canItBeRemoved: !isFixedType,
        grades: [
          { canItBeRemoved: !isFixedType },
          { canItBeRemoved: !isFixedType },
        ],
      },
    ]);
  }, [partialEvaluations, isFixedType, onChange]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);
  const percentage = sumPercentage(partialEvaluations);
  const percentageValid = percentage === 100;
  return (
    <>
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fs-20 fw-700 mt-2">
            {t('form.partialEvaluationGradeEntry.title')}
          </h2>
        </Col>
        <Col sm="auto" lg="auto" xs="auto">
          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle
              className="g-button"
              color="primary"
              size="md"
              caret
              disabled={isOnlineDeliveryModeAndFixedStructure}
            >
              <span>{t('form.partialEvaluationGradeEntry.addEvaluation')}</span>
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem onClick={addPartialEvaluation}>
                {t('form.partialEvaluationGradeEntry.addEvaluationNormal')}
              </DropdownItem>
              <DropdownItem onClick={addPartialEvaluationSubevaluations}>
                {t(
                  'form.partialEvaluationGradeEntry.addEvaluationSubevaluations',
                )}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </Col>
      </Row>

      <div className="mb-5 g-table-evaluations">
        <TableCollapsable<PartialEvaluation>
          columns={columns}
          isCollapsable={(row) => row.type === 'subevaluations'}
          striped={false}
          isStartCollapsed={() => true}
          secondColumn={{
            tdClassName: 'pt-0 px-4',
            columnName: 'grades',
            headerText: 'subevaluations-table-header',
            cellFormat: ({ row, index }) => {
              return (
                <div style={{ marginRight: '-0.75rem' }}>
                  <SubEvaluationsTable
                    partialEvaluation={row}
                    partialEvaluationIndex={index}
                  />
                </div>
              );
            },
          }}
          data={partialEvaluations}
          notResponsive
          noDefaultFoot
          footComponent={({ columns, data }) =>
            isOnlineDeliveryModeAndFixedStructure ? (
              <OnlineFixedFooter />
            ) : (
              <TotalPercentageFooter
                dataLength={data.length}
                columnsLength={columns.length}
                error={error}
                percentageValid={percentageValid}
                percentage={percentage}
              />
            )
          }
        />
        {error && !percentageValid && (
          <p className="text-danger mt-2">
            {t('form.msg.msgErrorTables.totalErrorOfTable')}
          </p>
        )}
      </div>
    </>
  );
}
